import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, ExternalLink, ImageIcon, MapPinned, Mic2, Search, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { projectApi } from '@/api/projectApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { deriveAssetLibraryState } from '@/lib/assetLibrary'
import type { AssetLibraryFilter, AssetLibraryItem, AssetLibraryType, Project } from '@/types/nf'

interface AssetLibraryPanelProps {
  simple?: boolean
  project: Project
  branchId: string
  onOpenExisting: (type: AssetLibraryType) => void
}

const iconByType = {
  character: UserRound,
  scene: MapPinned,
  prop: Box,
  voice: Mic2,
} satisfies Record<AssetLibraryType, typeof UserRound>

/** simple: 简易档位 —— 详情里只留来源等基本信息, 收起完整属性长列表。
 *  卡片上的前 3 条属性保留: 那是识别资产的关键信息, 不是噪音。 */
export function AssetLibraryPanel({ project, branchId, onOpenExisting, simple = false }: AssetLibraryPanelProps) {
  const { t } = useTranslation()
  const [selectedType, setSelectedType] = useState<AssetLibraryFilter>('all')
  const [query, setQuery] = useState('')
  const [selectedItem, setSelectedItem] = useState<AssetLibraryItem | null>(null)
  const assetLibraryQuery = useQuery({
    queryKey: ['asset-library', project.id, branchId],
    queryFn: () => projectApi.assetLibrary(project.id, { branchId }),
  })
  const filteredItems = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const items = assetLibraryQuery.data ?? []
    if (!needle) return items
    return items.filter((item) => `${item.name} ${item.source}`.toLowerCase().includes(needle))
  }, [assetLibraryQuery.data, query])
  const state = deriveAssetLibraryState(filteredItems, selectedType)
  useEffect(() => {
    if (state.activeType !== selectedType) setSelectedType(state.activeType)
  }, [selectedType, state.activeType])

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs value={state.activeType} onValueChange={(value) => setSelectedType(value as AssetLibraryFilter)}>
          <TabsList className="flex h-auto flex-wrap justify-start">
            {state.tabs.map((tab) => (
              <TabsTrigger key={tab.type} value={tab.type} className="gap-2">
                {t(`assetLibrary.types.${tab.type}`)}
                <Badge variant="secondary">{tab.count}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('assetLibrary.searchPlaceholder')}
            className="pl-9"
          />
        </div>
      </div>

      {assetLibraryQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} className="h-44 rounded-lg" />)}
        </div>
      ) : null}

      {assetLibraryQuery.error ? (
        <div className="rounded-lg border border-destructive/30 p-4 text-sm text-destructive">
          {t('assetLibrary.loadFailed')}
        </div>
      ) : null}

      {!assetLibraryQuery.isLoading && !assetLibraryQuery.error && state.items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('assetLibrary.empty')}
        </div>
      ) : null}

      {state.activeType === 'voice' ? (
        <VoiceAssetGroups items={state.items} onOpen={setSelectedItem} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {state.items.map((item) => (
            <AssetCard key={`${item.type}:${item.id}`} item={item} onOpen={() => setSelectedItem(item)} />
          ))}
        </div>
      )}

      <Sheet open={Boolean(selectedItem)} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {selectedItem ? (
            <>
              <SheetHeader>
                <SheetTitle>{selectedItem.name}</SheetTitle>
                <SheetDescription>{t(`assetLibrary.types.${selectedItem.type}`)}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 px-4 pb-4">
                <AssetPreview item={selectedItem} />
                <dl className="grid gap-3">
                  <DetailRow label={t('assetLibrary.source')} value={selectedItem.source} />
                  {selectedItem.source_chapter ? (
                    <DetailRow label={t('assetLibrary.sourceChapter')} value={String(selectedItem.source_chapter)} />
                  ) : null}
                  {simple ? null : assetAttributeEntries(selectedItem).map(([key, value]) => (
                    <DetailRow key={key} label={attributeLabel(key, t)} value={formatAttribute(value)} />
                  ))}
                </dl>
                {canOpenExisting(selectedItem.type) ? (
                  <Button type="button" variant="outline" onClick={() => onOpenExisting(selectedItem.type)}>
                    <ExternalLink className="size-4" />
                    {t('assetLibrary.openExisting')}
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function AssetCard({ item, onOpen }: { item: AssetLibraryItem; onOpen: () => void }) {
  const { t } = useTranslation()
  const Icon = iconByType[item.type]
  const gender = voiceGenderLabel(item.attributes?.gender)
  const age = voiceAgeLabel(item.attributes?.age_band)
  return (
    <Card className="overflow-hidden rounded-lg">
      <button type="button" className="block w-full text-left" onClick={onOpen}>
        <AssetPreview item={item} />
        <CardHeader className="space-y-2 p-4">
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline" className="gap-1">
              <Icon className="size-3.5" />
              {t(`assetLibrary.types.${item.type}`)}
            </Badge>
            {item.source_chapter ? <span className="text-xs text-muted-foreground">{t('assetLibrary.chapterShort', { chapter: item.source_chapter })}</span> : null}
          </div>
          {item.type === 'voice' ? (
            <div className="flex flex-wrap gap-1.5">
              {item.attributes?.builtin === true ? <Badge variant="secondary">{t('assetLibrary.attributes.builtin')}</Badge> : null}
              {gender ? <Badge variant="outline">{gender}</Badge> : null}
              {age ? <Badge variant="outline">{age}</Badge> : null}
            </div>
          ) : null}
          <CardTitle className="line-clamp-2 text-base tracking-normal">{item.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 px-4 pb-4 pt-0 text-xs text-muted-foreground">
          {assetAttributeEntries(item).slice(0, 3).map(([key, value]) => (
            <p key={key} className="truncate">
              <span className="text-foreground">{attributeLabel(key, t)}</span>: {formatAttribute(value)}
            </p>
          ))}
        </CardContent>
      </button>
    </Card>
  )
}

function VoiceAssetGroups({ items, onOpen }: { items: AssetLibraryItem[]; onOpen: (item: AssetLibraryItem) => void }) {
  const groups = groupVoiceAssets(items)
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.key} className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium tracking-normal">{group.label}</h3>
            <Badge variant="secondary">{group.items.length}</Badge>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {group.items.map((item) => (
              <AssetCard key={`${item.type}:${item.id}`} item={item} onOpen={() => onOpen(item)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function groupVoiceAssets(items: AssetLibraryItem[]) {
  const order = ['f:child', 'm:child', 'f:teen', 'm:teen', 'f:youth', 'm:youth', 'f:middle', 'm:middle', 'f:senior', 'm:senior', 'f:elder', 'm:elder', 'f:any', 'neutral:any', ':']
  const groups = new Map<string, AssetLibraryItem[]>()
  for (const item of items) {
    const gender = String(item.attributes?.gender ?? '').trim().toLowerCase()
    const age = String(item.attributes?.age_band ?? '').trim().toLowerCase()
    const key = `${gender}:${age}`
    groups.set(key, [...(groups.get(key) ?? []), item])
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      const ai = order.includes(a) ? order.indexOf(a) : order.length
      const bi = order.includes(b) ? order.indexOf(b) : order.length
      return ai - bi || a.localeCompare(b)
    })
    .map(([key, groupItems]) => ({ key, label: voiceGroupLabel(key), items: groupItems }))
}

function AssetPreview({ item }: { item: AssetLibraryItem }) {
  const Icon = iconByType[item.type]
  if (item.thumbnail_url) {
    return <img src={item.thumbnail_url} alt="" className="aspect-[4/3] w-full object-cover" />
  }
  return (
    <div className="grid aspect-[4/3] place-items-center bg-muted">
      {item.type === 'character' ? <ImageIcon className="size-8 text-muted-foreground" /> : <Icon className="size-8 text-muted-foreground" />}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div className="rounded-md border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words text-sm">{value}</dd>
    </div>
  )
}

function canOpenExisting(type: AssetLibraryType) {
  return type === 'character' || type === 'scene' || type === 'prop'
}

function assetAttributeEntries(item: AssetLibraryItem) {
  return Object.entries(item.attributes ?? {}).filter(([, value]) => formatAttribute(value) !== '')
}

function formatAttribute(value: unknown): string {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return ''
}

function voiceGenderLabel(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase()
  if (raw === 'm') return '男'
  if (raw === 'f') return '女'
  if (raw === 'neutral') return '中性'
  if (raw === 'any') return '通用'
  return raw
}

function voiceAgeLabel(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase()
  const labels: Record<string, string> = {
    child: '儿童',
    teen: '少年',
    youth: '青年',
    middle: '中年',
    senior: '长者',
    elder: '老年',
    any: '通用',
  }
  return labels[raw] ?? raw
}

function voiceGroupLabel(key: string): string {
  const [gender, age] = key.split(':')
  const parts = [voiceGenderLabel(gender), voiceAgeLabel(age)].filter(Boolean)
  return parts.length ? parts.join(' · ') : '未标注'
}

function attributeLabel(key: string, t: (key: string) => string) {
  const i18nKey = `assetLibrary.attributes.${key}`
  const label = t(i18nKey)
  return label === i18nKey ? key : label
}
